import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import FormControl from 'react-bootstrap/lib/FormControl';
import { List, Set } from 'immutable';
import moment from 'moment';
import _ from 'lodash';
import t from '../../lib/I18n';
import AddJourneyModalTravelForm from './AddJourneyModalTravelForm';
import AddJourneyModalTravelTable from './AddJourneyModalTravelTable';
import AddJourneyModalUsualDays from './AddJourneyModalUsualDays';
import { WeekDaysJourney, InitialWeekDayJourney } from '../../constants/AppConstants';
import Loading from '../general/Loading';
import TimeSelector from '../../components/search/dates/TimeSelector';
import Search from '../../records/Search';
import Timeband from '../../records/Timeband';
import StationService from '../../services/StationService';
import Checkbox from '../form/Checkbox';
import BAlert, { bAlertType } from '../general/BAlert';
import FeatureFlag from '../general/FeatureFlag';

const AddJourneyForm = ({
    businessDays,
    editingJourney,
    enableRegularJourneyCreation,
    errorWellText,
    isOCDRMode,
    outwardJourneys,
    remainingJourneys,
    saveRegularJourneyAlert,
    search,
    searchErrors,
    selectedJourneys,
    setBusinessDays,
    setDisabledSaveJourney,
    setJourneyData,
    setJourneyTime,
    setSaveRegularJourneyAlert,
    setStationFacilityAlert,
    setSearch,
    setWeekday,
    setSelectedJourneys,
    showErrorWell,
    stationFacilityAlert,
    weekday,
}) => {
    const [newTime, setNewTime] = useState(moment());
    const [newWeekday, setNewWeekday] = useState(weekday);
    const [isLoading, setIsLoading] = useState(false);
    const [isChangedStation, setIsChangedStation] = useState(false);

    const getTime = () => {
        const { hours, minutes } = newTime.toObject();

        let newDatetime = null;

        if (newWeekday === 'weekday') {
            let time = moment(newTime).startOf('isoWeek');

            const today = moment();

            /*
                If the Monday of the current week is today (today is Monday)
                or the Monday of the current week is in the past,
                we need to jump one week ahead else we won't get services returned for all
                possible search times
            */
            if (time.isBefore(today) || time.isSame(today, 'day')) {
                time = time.add(1, 'week');
            }

            newDatetime = time.set({ hours, minutes });
        } else {
            const dayInt = newWeekday === 'saturday' ? 6 : 7;
            newDatetime = moment().day(dayInt).set({ hours, minutes, second: 0 });
        }

        return newDatetime;
    };

    const setTime = () => {
        setJourneyTime(getTime());
    };

    useEffect(() => {
        setTime();
        // eslint-disable-next-line
    }, [newWeekday, newTime]);

    const hasOutwardJourneys = outwardJourneys && outwardJourneys.size > 0;

    useEffect(() => {
        const setStationsAndWeekday = async () => {
            const prepopulatedOriginNlc = editingJourney && editingJourney.origin.nlc;
            const prepopulatedDestinationNlc = editingJourney && editingJourney.destination.nlc;

            if (!hasOutwardJourneys && !isChangedStation) {
                const originStation = await StationService.get(prepopulatedOriginNlc);
                const destinationStation = await StationService.get(prepopulatedDestinationNlc);

                _.defer(() => setSearch(
                    search
                        .set('origin', originStation)
                        .set('destination', destinationStation)
                        .set('outboundDateTime', new Timeband({ datetime: getTime() })
                )));
            }
            setIsLoading(false);
        };

        if (editingJourney) {
            setIsLoading(true);
            setStationsAndWeekday();

            const originalTime = editingJourney.serviceTimes[0].split(':');

            const { journeyType } = editingJourney;
            setNewWeekday(journeyType);
            setWeekday(journeyType);
            setNewTime(newTime.set({
                hour: originalTime[0],
                minute: originalTime[1],
            }));
        }
        // eslint-disable-next-line
    }, [editingJourney]);

    const shouldShowStationAlertsCheckbox = !isOCDRMode || saveRegularJourneyAlert;

    if (isLoading) {
        return (<Loading message='Loading journey details' unwrapped standalone />);
    }

    return (
        <form className="journey-modal-form" onSubmit={e => e.preventDefault()}>
            <div className="journey-modal-weekday-select margin-left--xs margin-bottom--sm">
                <span aria-hidden="true">{t('auto-dr.account.subscribe.journeys.add')}</span>
                <FormControl
                    aria-label={t('auto-dr.account.subscribe.journeys.addJourney', { weekday })}
                    componentClass="select"
                    className="journey-modal-weekday margin-left--xs margin-right--xs padding--vert-xs"
                    name="weekday"
                    id="weekday"
                    value={weekday}
                    onChange={e => {
                        setWeekday(e.target.value);
                        setNewWeekday(e.target.value);
                    }}
                >
                    {WeekDaysJourney.map(day => (
                        <option key={day} value={day}>
                            {day[0].toUpperCase() + day.slice(1)}
                        </option>
                    ))}
                </FormControl>
                <span aria-hidden="true">{t('auto-dr.account.subscribe.journeys')}</span>
            </div>
            <Row>
                <Col xs={12}>
                    {(weekday === InitialWeekDayJourney) && (
                        <AddJourneyModalUsualDays
                            businessDays={businessDays}
                            setBusinessDays={setBusinessDays}
                        />
                    )}
                </Col>
            </Row>
            <Row className="journey-modal-info-row padding--horiz-sm margin-bottom--sm margin-left--xs margin-right--xs">
                <AddJourneyModalTravelForm
                    editingJourney={editingJourney}
                    search={search}
                    searchErrors={searchErrors}
                    setSearch={setSearch}
                    outwardJourneys={outwardJourneys}
                    setIsChangedStation={setIsChangedStation}
                />
            </Row>
            {showErrorWell && (<Row>
                <Col xs={12}>
                    <BAlert type={ bAlertType.DANGER }>{errorWellText}</BAlert>
                </Col>
            </Row>)}
            <Row className="journey-modal-info-row no-border">
                <Col xs={12} className="journey-modal-info-departing">
                    {t('auto-dr.account.subscribe.journeys.modal.departing')}
                    &nbsp;
                    <TimeSelector
                        className="disruption-alerts-service-selector__time"
                        combined
                        dateTime={search.outboundDateTime.datetime}
                        onChange={ datetime => {
                            setNewTime(datetime);
                        } }
                        ariaLabel={t('auto-dr.account.subscribe.journeys.departingAround')}
                    />
                </Col>
                <Col xs={12}>
                    {outwardJourneys && <AddJourneyModalTravelTable
                        editingJourney={editingJourney}
                        setJourneyData={setJourneyData}
                        outwardJourneys={outwardJourneys}
                        selectedJourneys={selectedJourneys}
                        setSelectedJourneys={setSelectedJourneys}
                        remainingJourneys={remainingJourneys}
                        businessDays={businessDays}
                        setDisabledSaveJourney={setDisabledSaveJourney}
                        weekday={weekday}
                        search={search}
                        setNewTime={setNewTime}
                        setSearch={setSearch}
                    />}
                </Col>
                    <FeatureFlag name="enableDisruptionAlerts">
                        {enableRegularJourneyCreation && <Col xs={12}>
                            <Checkbox
                                className="journey-modal-days-checkbox"
                                label={t('auto-dr.account.subscribe.info.regularJourneyAlert')}
                                aria-label="Set up regular journey alert for this journey"
                                onChange={() => {
                                    if (saveRegularJourneyAlert) {
                                        setStationFacilityAlert(false);
                                    }
                                    setSaveRegularJourneyAlert(!saveRegularJourneyAlert);
                                }}
                                checked={saveRegularJourneyAlert}
                            />
                        </Col>}
                        {shouldShowStationAlertsCheckbox && <Col xs={12}>
                            <Checkbox
                                className="journey-modal-days-checkbox"
                                label={t('auto-dr.account.subscribe.info.includeStationFacilityAlerts')}
                                aria-label="Include station facility alerts for this regular journey alert"
                                onChange={() => setStationFacilityAlert(!stationFacilityAlert)}
                                checked={stationFacilityAlert}
                            />
                        </Col>}
                </FeatureFlag>
                {(outwardJourneys && outwardJourneys.size > 0) && <Col xs={12} className="margin-bottom--xs margin-left--xs margin-right--xs">
                    <p className="padding-top">{t('auto-dr.account.subscribe.journeys.modal.travel.return.journey')}</p>
                </Col>}
            </Row>
        </form>
    );
};

AddJourneyForm.propTypes = {
    businessDays: PropTypes.array.isRequired,
    editingJourney: PropTypes.string,
    enableRegularJourneyCreation: PropTypes.bool,
    errorWellText: PropTypes.string,
    isOCDRMode: PropTypes.bool,
    outwardJourneys: PropTypes.instanceOf(List).isRequired,
    remainingJourneys: PropTypes.number.isRequired,
    saveRegularJourneyAlert: PropTypes.bool,
    search: PropTypes.instanceOf(Search).isRequired,
    searchErrors: PropTypes.instanceOf(Set),
    selectedJourneys: PropTypes.array.isRequired,
    setBusinessDays: PropTypes.func.isRequired,
    setDisabledSaveJourney: PropTypes.func.isRequired,
    setJourneyData: PropTypes.func.isRequired,
    setJourneyTime: PropTypes.func.isRequired,
    setSaveRegularJourneyAlert: PropTypes.func.isRequired,
    setSearch: PropTypes.func.isRequired,
    setSelectedJourneys: PropTypes.func.isRequired,
    setStationFacilityAlert: PropTypes.func.isRequired,
    setWeekday: PropTypes.func.isRequired,
    showErrorWell: PropTypes.bool,
    stationFacilityAlert: PropTypes.bool,
    weekday: PropTypes.string.isRequired,
};

export default AddJourneyForm;
